<script>
  import { createEventDispatcher } from "svelte";
  import { Modal, ModalBody, ModalFooter, ModalHeader } from "sveltestrap";
  import alertify from "alertifyjs";
  import backend from "../../../../../../server";
  import Dropdown from "../../../../ui/Dropdown.svelte";
  import InfoTooltip from "../../../../ui/InfoTooltip.svelte";
  import { generatePragmaticBonusCode } from "../../../../../utils/pragmaticFreeSpin.js";

  export let show = false;
  export let clients = [];

  const dispatch = createEventDispatcher();
  const ASSIGNATION_OPTIONS = [
    { value: "AUTO", label: "Automática" },
    { value: "MANUAL", label: "Manual" },
  ];
  let selectedClientId = "";
  let selectedGames = [];
  let selectedCurrencies = [];
  let clientCurrencies = [];
  let currencyRows = [];
  let currenciesError = "";
  let gamesLoading = false;
  let gamesError = "";
  let gameCatalog = [];
  let valuesLoading = false;
  let saving = false;
  let requestSequence = 0;
  let gamesRequestSequence = 0;
  let valuesRequestKey = "";
  let loadedClientCode = null;
  let validationMessage = "";
  let form = defaultForm();

  function localDateTimeValue(date) {
    date.setSeconds(0, 0);
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  }

  function minimumFutureDateTime() {
    return localDateTimeValue(new Date(Date.now() + 60000));
  }

  function defaultForm() {
    return {
      bonusCode: "",
      startDate: "",
      expirationDate: "",
      chipsNumber: "",
      maxBetLimit: "",
      assignationMode: "",
    };
  }

  const clean = (value) => String(value ?? "").trim();
  const currencyCode = (value) => clean(value).toUpperCase();
  const selectedClient = () => (Array.isArray(clients) ? clients : []).find(
    (client) => String(client?.id ?? client?.clientId) === String(selectedClientId),
  );
  const responseData = (response) => response?.data ?? response ?? {};
  function errorDescription(error, fallback) {
    const description = error?.response?.data?.description ?? error?.response?.data?.error ?? error?.message;
    if (!description) return fallback;
    const message = String(description);
    const dateError = /(date|fecha|expiration|expiry|validity|start).*(future|current|now|after|greater|later|expired|past)|must be.*(future|after)|should be.*(future|after)/i;
    if (dateError.test(message)) {
      return "La fecha y hora deben ser posteriores a la fecha y hora actual.";
    }
    return message;
  }

  function close() {
    show = false;
    dispatch("close");
  }

  function resetSelections() {
    selectedGames = [];
    selectedCurrencies = [];
    currencyRows = [];
    valuesRequestKey = "";
    validationMessage = "";
  }

  function setClientCurrency(client) {
    requestSequence++;
    clientCurrencies = [];
    resetSelections();
    const currency = currencyCode(client?.currency);
    clientCurrencies = currency ? [currency] : [];
    selectedCurrencies = [...clientCurrencies];
    currenciesError = client && !currency ? "El cliente no tiene una moneda configurada" : "";
  }

  async function loadClientGames(clientCode, currency) {
    const sequence = ++gamesRequestSequence;
    selectedGames = [];
    gameCatalog = [];
    gamesError = "";
    if (!clientCode || !currency) {
      gamesLoading = false;
      return;
    }
    gamesLoading = true;
    try {
      const response = await backend.games.listPragmaticFreeChipGames(clientCode, currency);
      if (sequence !== gamesRequestSequence) return;
      const payload = responseData(response);
      gameCatalog = (Array.isArray(payload?.gameList) ? payload.gameList : [])
        .map((game) => ({
          ...game,
          gameID: clean(game?.gameID ?? game?.gameId),
          gameName: clean(game?.gameName ?? game?.name),
        }))
        .filter((game) => game.gameID && game.gameName);
      if (!gameCatalog.length) gamesError = "El cliente no tiene juegos disponibles";
    } catch (error) {
      if (sequence !== gamesRequestSequence) return;
      gamesError = errorDescription(error, "No se pudieron cargar los juegos del cliente");
    } finally {
      if (sequence === gamesRequestSequence) gamesLoading = false;
    }
  }

  function reconcileCurrencyRows(values = [], multipliers = []) {
    const valuesByCurrency = new Map(
      values.map((item) => [
        currencyCode(item?.currency),
        [...(Array.isArray(item?.chipValues) ? item.chipValues : [])]
          .sort((left, right) => Number(left) - Number(right)),
      ]),
    );
    const multipliersByCurrency = new Map(
      multipliers.map((item) => [currencyCode(item?.currency), item?.multiplier]),
    );
    const previous = new Map(currencyRows.map((row) => [row.currency, row]));
    currencyRows = selectedCurrencies.map(currencyCode).map((currency) => {
      const old = previous.get(currency) || {};
      const options = valuesByCurrency.get(currency) || [];
      const chipValue = options.some((value) => Number(value) === Number(old.chipValue))
        ? old.chipValue
        : "";
      return {
        currency,
        chipValue,
        chipValues: options,
        maxWinLimit: old.maxWinLimit ?? "",
        multiplier: multipliersByCurrency.get(currency) ?? null,
      };
    });
  }

  async function loadChipConfiguration() {
    const clientCode = clean(selectedClient()?.code ?? selectedClient()?.clientCode);
    const currencies = selectedCurrencies.map(currencyCode).filter(Boolean);
    const games = selectedGames.map(clean).filter(Boolean);
    if (!clientCode || !currencies.length || !games.length) {
      currencyRows = [];
      return;
    }
    const key = JSON.stringify({ clientCode, currencies, games });
    if (key === valuesRequestKey) return;
    valuesRequestKey = key;
    const sequence = ++requestSequence;
    valuesLoading = true;
    validationMessage = "";
    try {
      const [valuesResponse, multipliersResponse] = await Promise.all([
        backend.freespin.getFreeChipValuesPrg(clientCode, {
          gameList: games,
          currencyList: currencies,
        }),
        backend.freespin.getFreeChipCurrencyMultipliersPrg(clientCode, {
          currencyList: currencies,
        }),
      ]);
      if (sequence !== requestSequence) return;
      const values = responseData(valuesResponse);
      const multipliers = responseData(multipliersResponse);
      reconcileCurrencyRows(values?.chipValuesPerCur || [], multipliers?.multipliersPerCur || []);
    } catch (error) {
      if (sequence !== requestSequence) return;
      currencyRows = [];
      validationMessage = errorDescription(error, "No se pudieron consultar los valores de chips");
    } finally {
      if (sequence === requestSequence) valuesLoading = false;
    }
  }

  function updateCurrencyRow(index, field, value) {
    currencyRows = currencyRows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: value } : row,
    );
  }

  function asIso(value) {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString();
  }

  function generateBonusCode() {
    const clientCode = clean(selectedClient()?.code ?? selectedClient()?.clientCode);
    if (!clientCode) {
      alertify.alert("Selecciona un cliente", "Debes seleccionar un cliente antes de generar el código.");
      return;
    }
    form = { ...form, bonusCode: generatePragmaticBonusCode(clientCode) };
  }

  function validate() {
    const clientCode = clean(selectedClient()?.code ?? selectedClient()?.clientCode);
    if (!clientCode) return "Selecciona un cliente.";
    if (!clean(form.bonusCode)) return "Ingresa el código del bonus.";
    if (!/^[A-Za-z0-9]+$/.test(clean(form.bonusCode))) return "El código del bonus solo puede contener letras y números.";
    if (clean(form.bonusCode).length > 100) return "El código del bonus admite máximo 100 caracteres.";
    if (!asIso(form.startDate)) return "Ingresa una fecha de inicio válida.";
    if (!selectedGames.length) return "Selecciona al menos un juego Live.";
    if (selectedGames.some((gameId) => !gameCatalog.some(
      (game) => clean(game?.gameID ?? game?.gameId) === clean(gameId) && clean(game?.gameName ?? game?.name),
    ))) return "Los juegos seleccionados deben tener un nombre válido.";
    if (!selectedCurrencies.length) return "Selecciona al menos una moneda.";
    if (!(Number(form.chipsNumber) > 0) || !Number.isInteger(Number(form.chipsNumber))) {
      return "La cantidad de chips debe ser un entero mayor que cero.";
    }
    if (form.maxBetLimit !== "" && (!(Number(form.maxBetLimit) > 0) || !Number.isInteger(Number(form.maxBetLimit)))) {
      return "El máximo de chips por ronda debe ser un entero mayor que cero.";
    }
    if (currencyRows.length !== selectedCurrencies.length || currencyRows.some((row) => !(Number(row.chipValue) > 0))) {
      return "Selecciona un valor de chip permitido para cada moneda.";
    }
    if (currencyRows.some((row) => !(Number(row.maxWinLimit) > 0))) {
      return "Ingresa un máximo de ganancia mayor que cero para cada moneda.";
    }
    const start = new Date(form.startDate).getTime();
    const expiration = new Date(form.expirationDate).getTime();
    const now = Date.now();
    if (start <= now) return "La fecha y hora de inicio deben ser posteriores a la fecha y hora actual.";
    if (!Number.isFinite(expiration)) return "Completa la fecha y hora de expiración.";
    if (expiration <= now) return "La fecha y hora de expiración deben ser posteriores a la fecha y hora actual.";
    if (expiration <= start) return "La expiración debe ser posterior al inicio.";
    if (expiration - start > 45 * 86400000) return "La expiración puede estar como máximo a 45 días del inicio.";
    return "";
  }

  async function createFreeChip() {
    const clientCode = clean(selectedClient()?.code ?? selectedClient()?.clientCode);
    if (!clean(form.bonusCode) && clientCode) {
      form = { ...form, bonusCode: generatePragmaticBonusCode(clientCode) };
    }
    validationMessage = validate();
    if (validationMessage) return;
    const params = {
      clientCode,
      bonusCode: clean(form.bonusCode),
      startDate: asIso(form.startDate),
      chipsNumber: Number(form.chipsNumber),
      assignationMode: form.assignationMode,
    };
    if (form.maxBetLimit !== "") params.maxBetLimit = Number(form.maxBetLimit);
    params.expirationDate = asIso(form.expirationDate);
    params.validityDate = params.expirationDate;
    const maxWinLimitPerCur = currencyRows.map((row) => ({
      currency: row.currency,
      maxWinLimit: Number(row.maxWinLimit),
    }));
    const payload = {
      gameList: selectedGames.map((gameId) => {
        const game = gameCatalog.find(
          (item) => clean(item?.gameID ?? item?.gameId) === clean(gameId),
        );
        return {
          gameId: clean(gameId),
          gameName: clean(game?.gameName ?? game?.name),
        };
      }),
      chipValuePerCur: currencyRows.map((row) => ({
        currency: row.currency,
        chipValue: Number(row.chipValue),
      })),
      maxWinLimitPerCur,
    };
    saving = true;
    try {
      const response = await backend.freespin.createFreeChipsPrg(params, payload);
      const result = responseData(response);
      if (String(result?.error) !== "0") throw new Error(result?.description || result?.error || "Error del proveedor");
      alertify.success("Campaña Free Chips creada correctamente");
      dispatch("created", result);
      close();
    } catch (error) {
      validationMessage = errorDescription(error, "No se pudo crear la campaña Free Chips");
    } finally {
      saving = false;
    }
  }

  $: currentClient = (Array.isArray(clients) ? clients : []).find(
    (client) => String(client?.id ?? client?.clientId) === String(selectedClientId),
  );
  $: clientCode = clean(currentClient?.code ?? currentClient?.clientCode);
  $: if (clientCode !== loadedClientCode) {
    loadedClientCode = clientCode;
    setClientCurrency(currentClient);
    loadClientGames(clientCode, currencyCode(currentClient?.currency));
  }
  $: configurationKey = JSON.stringify({ selectedGames, selectedCurrencies, clientCode });
  $: if (configurationKey) loadChipConfiguration();
  $: currencyOptions = clientCurrencies.map((currency) => ({ value: currency, label: currency }));
  $: gameOptions = Array.isArray(gameCatalog) ? gameCatalog : [];
</script>

<Modal isOpen={show} toggle={close} size="xl" centered={true} modalClassName="pragmatic-free-chip-modal">
  <ModalHeader toggle={close}>Crear campaña Pragmatic Free Chips</ModalHeader>
  <ModalBody>
    <section class="configuration-section" aria-labelledby="free-chip-general-title">
      <h6 id="free-chip-general-title" class="section-title">General</h6>
      <div class="section-box form-grid general-fields">
        <div class="field">
          <span class="field-label">Cliente <span class="required-marker" aria-hidden="true">*</span></span>
          <Dropdown
            options={clients}
            bind:value={selectedClientId}
            valueKey="id"
            labelKey="namesite"
            placeholder="Seleccionar cliente..."
            isStructuredDescription={true}
            paramStructuredName={["code", "namesite"]}
          />
        </div>
        <div class="field">
          <span class="field-label">
            Asignación
            <InfoTooltip
              info={[
                "Automática: el bonus se asignará automáticamente al momento de que el jugador abra el juego.",
                "Manual: el bonus se asignará manualmente desde el gestor de Bonos.",
              ]}
              ariaLabel="Información sobre los modos de asignación"
            />
          </span>
          <Dropdown options={ASSIGNATION_OPTIONS} bind:value={form.assignationMode} valueKey="value" labelKey="label" clearable={false} />
        </div>
      </div>
    </section>

    <section class="configuration-section" aria-labelledby="free-chip-details-title">
      <h6 id="free-chip-details-title" class="section-title">Detalles de campaña</h6>
      <div class="section-box form-grid campaign-fields">
        <div class="field">
          <span>
          Código del bonus
              <InfoTooltip
                info={["Opcional. Si no ingresas un código, al crear la campaña el sistema generará uno."]}
              />
            </span>
          <input
            class="form-control"
            maxlength="100"
            pattern="[A-Za-z0-9]+"
            title="Solo se permiten letras y números"
            bind:value={form.bonusCode}
          />
          <button type="button" class="generate-code-action" on:click={generateBonusCode}>
            Generar código
          </button>
        </div>
        <label class="field">
          <span>
            Fecha de inicio 
            <span class="required-marker" aria-hidden="true">*</span>
            <InfoTooltip
                info={["La fecha de inicio debe ser superior a la fecha y hora actual."]}
              />
          </span>
          <input class="form-control" type="datetime-local" min={minimumFutureDateTime()} bind:value={form.startDate} />
          <small class="field-help">
            Usar como referencia la fecha y hora actual del cliente, tienda u ORG objetivo.
          </small>
        </label>
        <label class="field">
          <span>Fecha y hora de expiración <span class="required-marker" aria-hidden="true">*</span></span>
          <input class="form-control" type="datetime-local" min={minimumFutureDateTime()} bind:value={form.expirationDate} />
        </label>
        <label class="field">
          <span>Cantidad de chips <span class="required-marker" aria-hidden="true">*</span></span>
          <input class="form-control" type="number" min="1" step="1" bind:value={form.chipsNumber} />
        </label>
        <label class="field">
          <span>
            Máximo de chips por ronda
            <InfoTooltip
              info={["Cantidad máxima de Free Chips que pueden apostarse en una ronda. Si no se especifica, el bonus no tendrá límite de apuesta por ronda."]}
              ariaLabel="Información sobre el máximo de chips por ronda"
            />
          </span>
          <input class="form-control" type="number" min="1" step="1" bind:value={form.maxBetLimit} />
        </label>
      </div>
    </section>

    <section class="section-box">
      <h6>Juegos y monedas</h6>
      <div class="two-columns">
        <div class="field">
          <span class="field-label">Mesas Live <span class="required-marker" aria-hidden="true">*</span></span>
          <Dropdown
            options={gameOptions}
            bind:value={selectedGames}
            valueKey="gameID"
            labelKey="gameName"
            multiple={true}
            placeholder={gamesLoading ? "Cargando juegos..." : "Seleccionar juegos..."}
            disabled={!selectedClientId || gamesLoading}
          />
          {#if gamesError}
            <small class="validation-message">{gamesError}</small>
          {/if}
        </div>
        <div class="field">
          <span class="field-label">Moneda <span class="required-marker" aria-hidden="true">*</span></span>
          <Dropdown
            options={currencyOptions}
            bind:value={selectedCurrencies}
            valueKey="value"
            labelKey="label"
            multiple={true}
            placeholder="Seleccionar moneda..."
            disabled={true}
          />
          {#if currenciesError}
            <small class="validation-message">{currenciesError}</small>
          {/if}
        </div>
      </div>

      {#if valuesLoading}
        <p class="state-message">Consultando valores permitidos por Pragmatic…</p>
      {:else if currencyRows.length}
        <div class="currency-table-wrapper">
          <table class="table table-sm table-bordered align-middle">
            <thead>
              <tr>
                <th>Valor del chip</th>
                <th>Máximo de ganancia</th>
                <th>Equivalencia EUR</th>
              </tr>
            </thead>
            <tbody>
              {#each currencyRows as row, index (row.currency)}
                <tr>
                  <td>
                    <Dropdown
                      options={row.chipValues.map((value) => ({ value, label: String(value) }))}
                      value={row.chipValue}
                      valueKey="value"
                      labelKey="label"
                      placeholder="Seleccionar..."
                      on:change={(event) => updateCurrencyRow(index, "chipValue", event.detail)}
                    />
                  </td>
                  <td>
                    <input class="form-control" type="number" min="0.000001" step="any" required value={row.maxWinLimit} on:input={(event) => updateCurrencyRow(index, "maxWinLimit", event.currentTarget.value)} />
                  </td>
                  <td>{row.multiplier == null ? "—" : `1 EUR = ${row.multiplier} ${row.currency}`}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </section>

    {#if validationMessage}<div class="alert alert-danger mb-0" role="alert">{validationMessage}</div>{/if}
  </ModalBody>
  <ModalFooter>
    <button type="button" class="btn btn-secondary" disabled={saving} on:click={close}>Cancelar</button>
    <button type="button" class="btn btn-success" disabled={saving || valuesLoading} on:click={createFreeChip}>
      {saving ? "Creando…" : "Crear Free Chip"}
    </button>
  </ModalFooter>
</Modal>

<style>
  .form-grid { display: grid; gap: 0.75rem; }
  .general-fields { grid-template-columns: repeat(2, minmax(180px, 1fr)); }
  .campaign-fields { grid-template-columns: repeat(3, minmax(180px, 1fr)); }
  .two-columns { display: grid; grid-template-columns: repeat(2, minmax(220px, 1fr)); gap: 0.75rem; }
  .field { display: flex; min-width: 0; flex-direction: column; gap: 0.3rem; color: #555; font-size: 0.8rem; font-weight: 400; }
  .field > span,
  .field-label { display: block; font-weight: 600; }
  .required-marker { color: #dc3545; }
  .field input { min-height: 38px; font-size: 0.875rem; }
  .configuration-section { margin-top: 1rem; }
  .section-title { margin: 0 0 0.5rem; color: #344054; font-size: 0.95rem; font-weight: 650; }
  .field-help, .generate-code-action { display: block; margin-top: 0.3rem; color: #0b5ed7; font-size: 0.75rem; font-weight: 600; line-height: 1.25; }
  .generate-code-action { padding: 0; border: 0; background: transparent; text-align: left; cursor: pointer; }
  .generate-code-action:hover { color: #084298; text-decoration: underline; }
  .section-box { padding: 0.85rem; border: 1px solid #e5e7eb; border-radius: 0.5rem; background: #fafbfc; }
  section.section-box { margin-top: 1rem; }
  .section-box h6 { margin: 0 0 0.75rem; color: #333; font-weight: 700; }
  :global(.pragmatic-free-chip-modal .trigger),
  :global(.pragmatic-free-chip-modal .trigger-label),
  :global(.pragmatic-free-chip-modal .search-input) { font-weight: 400; }
  .currency-table-wrapper {
    position: relative;
    z-index: 1;
    margin-top: 0.75rem;
    overflow: visible;
  }
  .currency-table-wrapper:focus-within { z-index: 20; }
  .currency-table-wrapper table { margin: 0; background: #fff; }
  .currency-table-wrapper td {
    min-width: 150px;
    overflow: visible;
  }
  .currency-table-wrapper td:focus-within {
    position: relative;
    z-index: 30;
  }
  .currency-table-wrapper :global(.select-box) { z-index: 40; }
  .currency-table-wrapper :global(.dropdown) {
    z-index: 100;
    min-width: 100%;
  }
  .state-message { margin: 0.75rem 0 0; color: #6c757d; text-align: center; }
  @media (max-width: 900px) {
    .general-fields, .campaign-fields { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 640px) { .general-fields, .campaign-fields, .two-columns { grid-template-columns: 1fr; } }
</style>
